import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, ShoppingBag, Heart, Share2, Minus, Plus, Truck, RotateCcw, Shield, Check, Sparkles } from 'lucide-react';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import ProductCard from '../components/product/ProductCard';
import toast from 'react-hot-toast';
import { getImageUrl } from '../utils/imageUrl';

interface Variant { id: number; size: string; color: string; color_hex: string; price: number; stock: number; }
interface Product {
  id: number; name: string; slug: string; price: number; compare_price?: number;
  description: string; featured_image: string; is_new?: boolean;
  variants: Variant[]; images: Array<{ id: number; image: string; alt_text: string }>;
  sizes?: string[]; colors?: Array<{ name: string; hex: string }>;
  brand?: string; material?: string; sku?: string; stock: number;
  category: { id: number; name: string; slug: string };
  product_type?: 'pet' | 'food' | 'accessory' | 'other';
  breed?: string;
  age?: string;
  gender?: string;
  vaccinated?: boolean;
  pet_type?: string;
  weight?: string;
  flavor?: string;
}

export default function ProductDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { addItem, openCart } = useCart();
  const [product, setProduct] = useState<Product | null>(null);
  const [related, setRelated] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [activeTab, setActiveTab] = useState('description');
  const [isWished, setIsWished] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    api.get(`/products/${slug}`)
      .then(r => {
        setProduct(r.data.product);
        setRelated(r.data.related || []);
        if (r.data.product.sizes?.[0]) setSelectedSize(r.data.product.sizes[0]);
        if (r.data.product.colors?.[0]) setSelectedColor(r.data.product.colors[0].name);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!product || !selectedSize || !selectedColor) { setSelectedVariant(null); return; }
    const variant = product.variants.find(v => v.size === selectedSize && v.color === selectedColor);
    setSelectedVariant(variant || null);
  }, [selectedSize, selectedColor, product]);

  const handleAddToCart = () => {
    if (!product) return;
    const sizes = product.sizes || [];
    const colors = product.colors || [];
    if (sizes.length > 0 && !selectedSize) { toast.error('Please select a size'); return; }
    if (colors.length > 0 && !selectedColor) { toast.error('Please select a color'); return; }

    const color = product.colors?.find(c => c.name === selectedColor);
    addItem({
      id: Date.now(),
      productId: product.id,
      variantId: selectedVariant?.id,
      name: product.name,
      price: selectedVariant?.price || product.price,
      image: getImageUrl(product.featured_image),
      size: selectedSize || undefined,
      color: selectedColor || undefined,
      colorHex: color?.hex,
      quantity,
    });
    toast.success('Added to cart!');
    openCart();
  };

  const formatPrice = (p: number) => `Rs.${p.toLocaleString()}`;
  const discount = product?.compare_price && product.compare_price > product.price
    ? Math.round(((product.compare_price - product.price) / product.compare_price) * 100) : 0;

  const allImages = product ? [
    getImageUrl(product.featured_image),
    ...product.images.map(i => getImageUrl(i.image))
  ].filter(Boolean) : [];

  if (loading) return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 animate-pulse">
        <div className="bg-gray-100 aspect-[4/5] rounded-[32px]" />
        <div className="space-y-6">
          <div className="h-10 bg-gray-100 w-3/4 rounded-full" />
          <div className="h-6 bg-gray-100 w-1/4 rounded-full" />
          <div className="h-20 bg-gray-100 w-full rounded-2xl" />
          <div className="h-12 bg-gray-100 w-1/2 rounded-2xl" />
        </div>
      </div>
    </div>
  );

  if (!product) return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-24 text-center">
      <h2 className="text-2xl font-serif font-extrabold text-primary">Product not found</h2>
      <Link to="/" className="mt-6 inline-block btn-primary">Back to Home</Link>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 md:py-16">
      {/* Breadcrumb Navigation */}
      <nav className="flex items-center gap-2 text-xs text-gray-550 mb-10 overflow-x-auto whitespace-nowrap pb-2">
        <Link to="/" className="hover:text-primary transition-colors font-medium">Home</Link>
        <ChevronRight size={12} className="text-gray-400" />
        <Link to={`/category/${product.category.slug}`} className="hover:text-primary transition-colors font-medium">{product.category.name}</Link>
        <ChevronRight size={12} className="text-gray-400" />
        <span className="text-primary font-bold truncate max-w-[200px]">{product.name}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start">
        {/* Left Side: Images Gallery */}
        <div className="lg:col-span-7 flex flex-col md:flex-row gap-4">
          {/* Thumbnails (Desktop layout) */}
          {allImages.length > 1 && (
            <div className="hidden md:flex flex-col gap-3 w-20 flex-shrink-0">
              {allImages.slice(0, 5).map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImage(i)}
                  className={`w-20 h-24 overflow-hidden rounded-2xl border-2 transition-all ${activeImage === i ? 'border-accent shadow-md scale-102' : 'border-gray-100 hover:border-accent/40'}`}
                >
                  <img src={img} alt={`Thumbnail ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {/* Main Hero Image */}
          <div className="flex-1 relative overflow-hidden bg-[#FCFAF7] border border-gray-100 rounded-[32px] shadow-sm group">
            <div className="absolute top-5 left-5 flex flex-col gap-2 z-10">
              {product.is_new && <span className="bg-primary text-white text-[9px] font-extrabold px-3 py-1 rounded-full uppercase tracking-wider shadow-md">New</span>}
              {discount > 0 && <span className="bg-red-650 text-white text-[9px] font-extrabold px-3 py-1 rounded-full tracking-wider shadow-md">-{discount}% OFF</span>}
            </div>
            
            <img
              src={allImages[activeImage] || product.featured_image}
              alt={product.name}
              className="w-full aspect-[4/5] object-cover transition-transform duration-700 group-hover:scale-102"
            />

            {/* Mobile Thumbnails Scrollbar */}
            {allImages.length > 1 && (
              <div className="flex md:hidden gap-2.5 px-5 py-4 overflow-x-auto bg-white/70 backdrop-blur-md border-t border-gray-100">
                {allImages.map((img, i) => (
                  <button 
                    key={i} 
                    onClick={() => setActiveImage(i)} 
                    className={`w-14 h-16 rounded-xl overflow-hidden flex-shrink-0 border-2 transition-all ${activeImage === i ? 'border-accent scale-102 shadow-sm' : 'border-transparent'}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Side: Product Details & Shopping Options */}
        <div className="lg:col-span-5 space-y-6">
          <div className="space-y-2">
            <span className="text-[10px] font-extrabold text-accent uppercase tracking-[0.25em]">{product.brand || 'Royal Pet Store'}</span>
            <h1 className="text-2xl md:text-3.5xl font-serif font-extrabold text-primary leading-tight">{product.name}</h1>
            
            <div className="flex items-center gap-3.5 pt-2">
              <span className={`text-2.5xl font-serif font-black ${discount > 0 ? 'text-red-600' : 'text-primary'}`}>
                {formatPrice(selectedVariant?.price || product.price)}
              </span>
              {product.compare_price && product.compare_price > product.price && (
                <span className="text-base text-gray-400 line-through font-semibold">{formatPrice(product.compare_price)}</span>
              )}
            </div>
            
            <div className="flex items-center gap-2 pt-1">
              <span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-xs text-green-600 font-bold uppercase tracking-wider">
                {(selectedVariant?.stock || product.stock) > 0 ? 'Certified Available' : 'Temporarily Unavailable'}
              </span>
            </div>
          </div>

          <div className="w-full h-[1px] bg-gray-100" />

          {/* Pet specific parameters */}
          {product.product_type === 'pet' && (
            <div className="bg-white p-6 rounded-[24px] border border-gray-100/80 space-y-4 shadow-premium">
              <div className="flex items-center justify-between border-b border-gray-50 pb-3">
                <h3 className="font-bold font-serif text-primary text-sm uppercase tracking-wider flex items-center gap-1.5">
                  <Sparkles size={14} className="text-accent" /> Pet Specifications
                </h3>
                <span className="text-[10px] font-extrabold text-green-600 bg-green-50 px-2.5 py-1 rounded-full border border-green-500/10">Vet Approved</span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-xs font-semibold text-gray-650">
                <div className="bg-[#FCFAF7] p-3 rounded-xl"><span className="text-gray-400 block text-[9px] uppercase tracking-wider mb-0.5">Breed Lineage</span> {product.breed || 'Pure Breed'}</div>
                <div className="bg-[#FCFAF7] p-3 rounded-xl"><span className="text-gray-400 block text-[9px] uppercase tracking-wider mb-0.5">Age</span> {product.age || 'Puppy'}</div>
                <div className="bg-[#FCFAF7] p-3 rounded-xl"><span className="text-gray-400 block text-[9px] uppercase tracking-wider mb-0.5">Gender</span> {product.gender || 'Male'}</div>
                <div className="bg-[#FCFAF7] p-3 rounded-xl"><span className="text-gray-400 block text-[9px] uppercase tracking-wider mb-0.5">Pet Category</span> {product.pet_type || 'Dog'}</div>
                <div className="col-span-2 bg-[#FCFAF7] p-3 rounded-xl flex items-center gap-2">
                  <Check size={14} className="text-green-600 flex-shrink-0" />
                  <span>{product.vaccinated ? 'Fully Dewormed & Vaccinated (Record Provided)' : 'Deworming & Basic Vaccinations Complete'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Food parameters */}
          {product.product_type === 'food' && (
            <div className="bg-white p-6 rounded-[24px] border border-gray-100/80 space-y-4 shadow-premium">
              <div className="flex items-center justify-between border-b border-gray-50 pb-3">
                <h3 className="font-bold font-serif text-primary text-sm uppercase tracking-wider flex items-center gap-1.5">
                  🛡️ Nutritional Formulation
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-4 text-xs font-semibold text-gray-650">
                <div className="bg-[#FCFAF7] p-3 rounded-xl"><span className="text-gray-400 block text-[9px] uppercase tracking-wider mb-0.5">Brand Maker</span> {product.brand || 'N/A'}</div>
                <div className="bg-[#FCFAF7] p-3 rounded-xl"><span className="text-gray-400 block text-[9px] uppercase tracking-wider mb-0.5">Pet Specifics</span> {product.pet_type || 'Dogs & Cats'}</div>
                <div className="bg-[#FCFAF7] p-3 rounded-xl"><span className="text-gray-400 block text-[9px] uppercase tracking-wider mb-0.5">Net Bag Weight</span> {product.weight || 'N/A'}</div>
                <div className="bg-[#FCFAF7] p-3 rounded-xl"><span className="text-gray-400 block text-[9px] uppercase tracking-wider mb-0.5">Flavor Profile</span> {product.flavor || 'Original Formulation'}</div>
              </div>
            </div>
          )}

          {/* Accessory parameters */}
          {product.product_type === 'accessory' && (
            <div className="bg-white p-6 rounded-[24px] border border-gray-100/80 space-y-4 shadow-premium">
              <div className="flex items-center justify-between border-b border-gray-50 pb-3">
                <h3 className="font-bold font-serif text-primary text-sm uppercase tracking-wider">
                  📐 Product Specifications
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-4 text-xs font-semibold text-gray-650">
                <div className="bg-[#FCFAF7] p-3 rounded-xl"><span className="text-gray-400 block text-[9px] uppercase tracking-wider mb-0.5">Brand Maker</span> {product.brand || 'N/A'}</div>
                <div className="bg-[#FCFAF7] p-3 rounded-xl"><span className="text-gray-400 block text-[9px] uppercase tracking-wider mb-0.5">Target Pets</span> {product.pet_type || 'Cats & Dogs'}</div>
              </div>
            </div>
          )}

          {/* Size Selectors (Pills) */}
          {product.product_type !== 'pet' && product.sizes && product.sizes.length > 0 && (
            <div className="space-y-3">
              <label className="text-xs font-extrabold text-primary uppercase tracking-wider">
                {product.product_type === 'food' ? 'Available Weight:' : 'Select Size:'} <span className="text-accent">{selectedSize}</span>
              </label>
              <div className="flex flex-wrap gap-2.5">
                {product.sizes.map(size => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={`h-11 px-5 border text-xs font-bold uppercase tracking-wider rounded-xl transition-all ${selectedSize === size ? 'border-primary bg-primary text-white shadow-md' : 'border-gray-200 text-gray-700 bg-white hover:border-accent'}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Color Selector (Rounds) */}
          {product.product_type !== 'pet' && product.colors && product.colors.length > 0 && (
            <div className="space-y-3">
              <label className="text-xs font-extrabold text-primary uppercase tracking-wider block">Select Color: <span className="text-accent">{selectedColor}</span></label>
              <div className="flex flex-wrap gap-3">
                {product.colors.map(color => (
                  <button
                    key={color.name}
                    onClick={() => setSelectedColor(color.name)}
                    title={color.name}
                    className={`w-9 h-9 rounded-full border-4 transition-all relative ${selectedColor === color.name ? 'border-primary scale-110 shadow-md' : 'border-white shadow-sm hover:scale-105'}`}
                    style={{ backgroundColor: color.hex }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Quantity Counter */}
          {product.product_type !== 'pet' && (
            <div className="space-y-3">
              <label className="text-xs font-extrabold text-primary uppercase tracking-wider block">Select Quantity:</label>
              <div className="flex items-center gap-3">
                <div className="flex items-center border border-gray-200 rounded-xl bg-white p-1">
                  <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="w-9 h-9 flex items-center justify-center text-gray-500 hover:text-primary active:scale-75 transition-all">
                    <Minus size={13} />
                  </button>
                  <span className="w-10 text-center text-xs font-bold text-primary">{quantity}</span>
                  <button onClick={() => setQuantity(q => q + 1)} className="w-9 h-9 flex items-center justify-center text-gray-500 hover:text-primary active:scale-75 transition-all">
                    <Plus size={13} />
                  </button>
                </div>
                <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">
                  Subtotal: {formatPrice((selectedVariant?.price || product.price) * quantity)}
                </span>
              </div>
            </div>
          )}

          {/* Checkout CTA Actions */}
          <div className="space-y-3 pt-2">
            <button
              onClick={handleAddToCart}
              className="w-full bg-primary hover:bg-accent text-white py-4 text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 rounded-xl shadow-premium hover:-translate-y-0.5 active:scale-98 transition-all"
            >
              <ShoppingBag size={16} />
              {product.product_type === 'pet' ? 'Adopt Companion Friend' : 'Add to Shopping Cart'}
            </button>
            <button
              onClick={handleAddToCart}
              className="w-full border-2 border-primary text-primary hover:bg-primary hover:text-white py-4 text-xs font-bold uppercase tracking-widest rounded-xl hover:-translate-y-0.5 active:scale-98 transition-all"
            >
              {product.product_type === 'pet' ? 'Instant Adoption Inquiry' : 'Instant Buy It Now'}
            </button>
          </div>

          {/* Share & Wishlist Row */}
          <div className="flex items-center gap-5 pt-1.5 text-xs font-bold uppercase tracking-wider text-gray-500">
            <button
              onClick={() => { setIsWished(!isWished); toast.success(isWished ? 'Removed' : 'Added to Wishlist'); }}
              className={`flex items-center gap-2 hover:text-primary transition-colors ${isWished ? 'text-red-500' : ''}`}
            >
              <Heart size={15} className={isWished ? 'fill-current' : ''} />
              Wishlist
            </button>
            <button
              onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success('Adoption Link copied!'); }}
              className="flex items-center gap-2 hover:text-primary transition-colors"
            >
              <Share2 size={15} />
              Share Link
            </button>
          </div>

          <div className="w-full h-[1px] bg-gray-100" />

          {/* Delivery & Trust badges */}
          <div className="space-y-3 text-xs font-semibold text-gray-600">
            {product.product_type === 'pet' ? (
              <>
                <div className="flex gap-3">
                  <div className="p-2 bg-[#FCFAF7] rounded-lg flex-shrink-0 text-accent"><Truck size={15} /></div>
                  <div><span className="font-bold text-primary">Safe Climate Transport</span>: Transported in temperature-regulated vans with professional supervision.</div>
                </div>
                <div className="flex gap-3">
                  <div className="p-2 bg-[#FCFAF7] rounded-lg flex-shrink-0 text-accent"><Shield size={15} /></div>
                  <div><span className="font-bold text-primary">Certified Vet Inspections</span>: Official vaccination logbook, microchip details, and pedigree records.</div>
                </div>
              </>
            ) : (
              <>
                <div className="flex gap-3">
                  <div className="p-2 bg-[#FCFAF7] rounded-lg flex-shrink-0 text-accent"><Truck size={15} /></div>
                  <div><span className="font-bold text-primary">Free Dispatch Transit</span> on orders above Rs. 10,000. Expected 3-5 days delivery.</div>
                </div>
                <div className="flex gap-3">
                  <div className="p-2 bg-[#FCFAF7] rounded-lg flex-shrink-0 text-accent"><RotateCcw size={15} /></div>
                  <div><span className="font-bold text-primary">7-Day Return Policy</span> on completely sealed food pouches or unused items.</div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabs Specifications Details */}
      <div className="mt-20 border-t border-gray-100">
        <div className="flex gap-8 border-b border-gray-100">
          {['description', 'shipping'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors ${activeTab === tab ? 'border-primary text-primary' : 'border-transparent text-gray-400 hover:text-primary'}`}
            >
              {tab === 'shipping' ? (product.product_type === 'pet' ? 'Adoption & Transport Protocol' : 'Shipping & Returns') : 'Description'}
            </button>
          ))}
        </div>
        
        <div className="py-8 max-w-3xl text-sm leading-relaxed text-gray-650 font-medium">
          {activeTab === 'description' ? (
            <div className="space-y-4">
              {product.sku && <p className="text-xs text-accent font-extrabold uppercase tracking-wider">{product.product_type === 'pet' ? `Companion Registry SKU: ${product.sku}` : `Product SKU: ${product.sku}`}</p>}
              {product.description ? (
                <p>{product.description}</p>
              ) : (
                product.product_type === 'pet' ? (
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Certified deworming and health clearances complete</li>
                    <li>Microchipped and registered breeder pedigree</li>
                    <li>Highly socialized, active and friendly temperament</li>
                    <li>Veterinary health clearance report provided at handover</li>
                  </ul>
                ) : (
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Premium organic formulation using human-grade ingredients</li>
                    <li>Maintains shiny fur coat, healthy joints and digestion</li>
                    <li>No artificial coloring, preservatives, or GMO filters</li>
                    <li>Recommended by certified veterinarians nationwide</li>
                  </ul>
                )
              )}
            </div>
          ) : (
            product.product_type === 'pet' ? (
              <div className="space-y-4 text-gray-600">
                <p><strong>Safe Climate Shipping:</strong> Royal Pet Store handles pet transfers via temperature-controlled vans. Handlers supervise pets, providing feeding, hydration, and breaks to keep animals stress-free.</p>
                <p><strong>Medical Records:</strong> On handover, you receive the original vaccine certificate, microchip registry card, and health clearance certificates from certified veterinarians.</p>
                <p><strong>Breeder Pedigree:</strong> Pedigree registry certifications and parents lineage documentation will be provided to confirm the pet breed authenticity.</p>
              </div>
            ) : (
              <div className="space-y-4 text-gray-600">
                <p><strong>Shipping Dispatch:</strong> Free climate-regulated transport for products above Rs. 10,000. Under Rs. 10,000, standard dispatch pricing applies.</p>
                <p><strong>Transit Time:</strong> Deliveries typically complete within 3-5 working days in Lahore, Karachi, Islamabad, and other cities.</p>
                <p><strong>Returns:</strong> Returns must be initiated within 7 days. Feed bags must have seal verification intact to clear safety regulations.</p>
              </div>
            )
          )}
        </div>
      </div>

      {/* Related Products Feed */}
      {related.length > 0 && (
        <section className="mt-20 border-t border-gray-100 pt-16">
          <div className="text-center mb-12">
            <span className="text-accent text-[10px] font-extrabold uppercase tracking-[0.25em]">Recommended companions</span>
            <h2 className="text-2xl md:text-3xl font-serif font-extrabold text-primary mt-1">You May Also Like</h2>
            <div className="w-10 h-0.5 bg-accent/40 mx-auto mt-4" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {related.slice(0, 4).map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
